import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STREAMING_ENABLED_DEFAULT = true;

interface StreamingContextType {
  isStreamingEnabled: boolean;
  toggleStreaming: (value?: boolean) => void;
  isStreamInProgress: boolean;
  setStreamInProgress: (value: boolean) => void;
}

const StreamingContext = createContext<StreamingContextType | undefined>(undefined);

export const StreamingProvider = ({ children }: { children: React.ReactNode }) => {
  const [isStreamingEnabled, setIsStreamingEnabled] = useState<boolean>(STREAMING_ENABLED_DEFAULT);
  const [isStreamInProgress, setStreamInProgress] = useState<boolean>(false);

  const toggleStreaming = useCallback((value?: boolean) => {
    setIsStreamingEnabled(prev => (value !== undefined ? value : !prev));
  }, []);

  // Memoised: `isStreamInProgress` is now live (published by Chat.tsx) and this
  // provider sits near the app root, so a fresh value object every render would
  // re-render the whole subtree on every stream start/stop.
  const value = useMemo(
    () => ({ isStreamingEnabled, toggleStreaming, isStreamInProgress, setStreamInProgress }),
    [isStreamingEnabled, toggleStreaming, isStreamInProgress],
  );

  return (
    <StreamingContext.Provider value={value}>
      {children}
    </StreamingContext.Provider>
  );
};

export const useStreaming = (): StreamingContextType => {
  const context = useContext(StreamingContext);
  if (context === undefined) {
    throw new Error('useStreaming must be used within a StreamingProvider');
  }
  return context;
};
